import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../../app/store';
import { tenantService, CreateTenantRequest, CreateAdminUserRequest } from '../../services/tenantService';

export interface Tenant {
  id: string;
  name: string;
  slug: string;
  domain?: string;
  logo?: string;
  status: 'active' | 'inactive' | 'suspended';
  plan: 'free' | 'starter' | 'professional' | 'enterprise';
  employeeCount: number;
  createdAt: string;
  updatedAt: string;
  settings?: {
    timezone?: string;
    language?: string;
    currency?: string;
  };
}

export interface OnboardingFormData {
  organizationName: string;
  slug: string;
  domain: string;
  plan: 'free' | 'starter' | 'professional' | 'enterprise';
  adminName: string;
  adminEmail: string;
  adminPassword: string;
  timezone: string;
  language: string;
  currency: string;
  // Billing subscription (platform-set). Configured in the Plan & Billing step and
  // provisioned in billing-service after the tenant is created.
  billingAmount: string;
  billingCycle: 'monthly' | 'yearly';
  billingCurrency: string;
  billingEmail: string;
}

interface OnboardingState {
  currentStep: number;
  formData: OnboardingFormData;
  isSlugAvailable: boolean | null;
  isValidatingSlug: boolean;
  slugSuggestion: string | null;
  isCreatingTenant: boolean;
  createdTenantId: string | null;
  error: string | null;
}

const initialOnboardingFormData: OnboardingFormData = {
  organizationName: '',
  slug: '',
  domain: '',
  plan: 'starter',
  adminName: '',
  adminEmail: '',
  adminPassword: '',
  timezone: 'UTC',
  language: 'en',
  currency: 'USD',
  billingAmount: '',
  billingCycle: 'monthly',
  billingCurrency: 'TZS',
  billingEmail: '',
};

const initialOnboardingState: OnboardingState = {
  currentStep: 0,
  formData: initialOnboardingFormData,
  isSlugAvailable: null,
  isValidatingSlug: false,
  slugSuggestion: null,
  isCreatingTenant: false,
  createdTenantId: null,
  error: null,
};

interface TenantState {
  tenants: Tenant[];
  currentTenant: Tenant | null;
  isLoading: boolean;
  error: string | null;
  totalCount: number;
  onboarding: OnboardingState;
}

const initialState: TenantState = {
  tenants: [],
  currentTenant: null,
  isLoading: false,
  error: null,
  totalCount: 0,
  onboarding: initialOnboardingState,
};

// Async thunks
export const fetchTenants = createAsyncThunk(
  'tenants/fetchAll',
  async () => {
    // This would call the API
    return { tenants: [], totalCount: 0 };
  }
);

export const validateSlug = createAsyncThunk(
  'tenants/validateSlug',
  async (slug: string, { rejectWithValue }) => {
    try {
      // Use mock in development mode
      if (import.meta.env.DEV) {
        return await tenantService.mockValidateSlug(slug);
      }
      return await tenantService.validateSlug(slug);
    } catch (error) {
      return rejectWithValue('Failed to validate slug');
    }
  }
);

export const createTenant = createAsyncThunk(
  'tenants/create',
  async (
    payload: { tenant: CreateTenantRequest; adminUser: CreateAdminUserRequest },
    { rejectWithValue }
  ) => {
    try {
      // Use mock in development mode
      if (import.meta.env.DEV) {
        const tenant = await tenantService.mockCreateTenant(payload.tenant);
        await tenantService.mockCreateAdminUser(tenant.id, payload.adminUser);
        return tenant;
      }
      // Single call: the backend creates the tenant AND its admin user (and provisions
      // the owner's employee record via tenant.created) in one transaction.
      const tenant = await tenantService.createTenant(payload.tenant, payload.adminUser);
      return tenant;
    } catch (error: any) {
      // Surface the backend's actual message so failures are diagnosable in the UI.
      const msg =
        error?.response?.data?.message ??
        (Array.isArray(error?.response?.data?.errors) ? error.response.data.errors.join(', ') : null) ??
        error?.message ??
        'Failed to create tenant';
      return rejectWithValue(typeof msg === 'string' ? msg : 'Failed to create tenant');
    }
  }
);

const tenantSlice = createSlice({
  name: 'tenant',
  initialState,
  reducers: {
    setCurrentTenant: (state, action: PayloadAction<Tenant>) => {
      state.currentTenant = action.payload;
    },
    clearTenants: (state) => {
      state.tenants = [];
      state.currentTenant = null;
      state.totalCount = 0;
    },
    // Onboarding reducers
    setOnboardingStep: (state, action: PayloadAction<number>) => {
      state.onboarding.currentStep = action.payload;
    },
    updateOnboardingFormData: (state, action: PayloadAction<Partial<OnboardingFormData>>) => {
      state.onboarding.formData = { ...state.onboarding.formData, ...action.payload };
      // Reset slug validation when slug changes
      if ('slug' in action.payload) {
        state.onboarding.isSlugAvailable = null;
        state.onboarding.slugSuggestion = null;
      }
    },
    resetOnboarding: (state) => {
      state.onboarding = initialOnboardingState;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch tenants
      .addCase(fetchTenants.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTenants.fulfilled, (state, action) => {
        state.isLoading = false;
        state.tenants = action.payload.tenants;
        state.totalCount = action.payload.totalCount;
      })
      .addCase(fetchTenants.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch tenants';
      })
      // Validate slug
      .addCase(validateSlug.pending, (state) => {
        state.onboarding.isValidatingSlug = true;
        state.onboarding.isSlugAvailable = null;
        state.onboarding.slugSuggestion = null;
      })
      .addCase(validateSlug.fulfilled, (state, action) => {
        state.onboarding.isValidatingSlug = false;
        state.onboarding.isSlugAvailable = action.payload.available;
        state.onboarding.slugSuggestion = action.payload.suggestion || null;
      })
      .addCase(validateSlug.rejected, (state) => {
        state.onboarding.isValidatingSlug = false;
        state.onboarding.isSlugAvailable = null;
      })
      // Create tenant
      .addCase(createTenant.pending, (state) => {
        state.onboarding.isCreatingTenant = true;
        state.onboarding.error = null;
      })
      .addCase(createTenant.fulfilled, (state, action) => {
        state.onboarding.isCreatingTenant = false;
        state.onboarding.createdTenantId = action.payload.id;
        // Add the new tenant to the list
        state.tenants.push(action.payload);
        state.totalCount += 1;
      })
      .addCase(createTenant.rejected, (state, action) => {
        state.onboarding.isCreatingTenant = false;
        state.onboarding.error = (action.payload as string) || 'Failed to create tenant';
      });
  },
});

export const {
  setCurrentTenant,
  clearTenants,
  setOnboardingStep,
  updateOnboardingFormData,
  resetOnboarding,
} = tenantSlice.actions;

// Tenant selectors
export const selectTenants = (state: RootState) => state.tenant.tenants;
export const selectCurrentTenant = (state: RootState) => state.tenant.currentTenant;

// Onboarding selectors
export const selectOnboardingStep = (state: RootState) => state.tenant.onboarding.currentStep;
export const selectOnboardingFormData = (state: RootState) => state.tenant.onboarding.formData;
export const selectIsSlugAvailable = (state: RootState) => state.tenant.onboarding.isSlugAvailable;
export const selectIsValidatingSlug = (state: RootState) => state.tenant.onboarding.isValidatingSlug;
export const selectSlugSuggestion = (state: RootState) => state.tenant.onboarding.slugSuggestion;
export const selectIsCreatingTenant = (state: RootState) => state.tenant.onboarding.isCreatingTenant;
export const selectCreatedTenantId = (state: RootState) => state.tenant.onboarding.createdTenantId;
export const selectOnboardingError = (state: RootState) => state.tenant.onboarding.error;

export default tenantSlice.reducer;